// What each column of the analytics tables holds, as the code that loads them fills it in. A test keeps this in step
// with STRADA_CALLS_DDL and STRADA_CHATS_DDL. Timestamps are ISO text in UTC, such as 2026-09-25T14:03:11.123Z.
const LOADED_LATE = 'Null until the dashboard finishes loading transcripts.';
const QA_SENTIMENT = "The latest QA review's sentiment label; labels are the organization's own.";
const RUBRIC_RESULTS = 'QA pass/fail by criterion id. Prefer rubric_outcomes.';
const RUBRIC_OUTCOMES = 'QA pass/fail by criterion name: {"Criterion name": true}. {} when QA ran without criteria, null when it never ran.';
export const ANALYTICS_COLUMNS = {
    strada_calls_history: [
        { name: 'call_id', type: 'text', note: 'Unique id of the call.' },
        { name: 'agent_id', type: 'text', note: 'The agent that took or made the call.' },
        { name: 'agent_name', type: 'text', note: "The agent's name." },
        { name: 'created_at', type: 'text', note: 'When the call was created.' },
        { name: 'status', type: 'text', note: 'Always "ended": only finished calls are loaded.' },
        { name: 'type', type: 'text', note: 'inboundPhoneCall, outboundPhoneCall or webCall.' },
        {
            name: 'campaign_name',
            type: 'text',
            note: 'The campaign that placed the call; a leading "_" marks a test call.'
        },
        { name: 'started_at', type: 'text', note: 'When the call connected; null if it never did.' },
        { name: 'recording_url', type: 'text', note: 'Where the recording is stored.' },
        { name: 'ended_at', type: 'text', note: 'When the call ended.' },
        { name: 'customer_number', type: 'text', note: "The caller's or callee's phone number." },
        {
            name: 'ended_reason',
            type: 'text',
            note: "The organization's own label for how the call ended, such as Connected, Transferred, No Answer or a custom reason. Differs per organization."
        },
        {
            name: 'transfer_destination',
            type: 'text',
            note: 'Where the call was transferred: a number, "number, extension" or "SIP Transfer".'
        },
        { name: 'summary', type: 'text', note: 'The AI summary of the call.' },
        {
            name: 'analysis',
            type: 'jsonb',
            note: "The agent's call analysis fields, filled in after the call. Keys differ per agent."
        },
        {
            name: 'transcript',
            type: 'jsonb',
            note: `An array of messages: {role: "user" | "bot" | "system", message}, {role: "tool_calls", toolCalls: [{function: {name, arguments}}]} with arguments as JSON text, and {role: "tool_call_result", name, result} with result as text. ${LOADED_LATE}`
        },
        {
            name: 'call_variables',
            type: 'jsonb',
            note: 'Values passed in when the call started, such as campaign or prompt variables.'
        },
        {
            name: 'session_variables',
            type: 'jsonb',
            note: "Values the agent's Set Variable steps stored during the call, latest value per key. Null when none."
        },
        { name: 'phone_number', type: 'text', note: "The organization's own number the call used." },
        {
            name: 'campaign_parent_call_ids',
            type: 'jsonb',
            note: 'For campaign retries, the earlier calls in the chain, nearest first; [] otherwise.'
        },
        {
            name: 'is_connected',
            type: 'boolean',
            note: "Whether the call counts as connected under this dashboard's Connected Rate settings; false for every call when the dashboard has none."
        },
        {
            name: 'is_converted',
            type: 'boolean',
            note: "Whether the call counts as converted under this dashboard's Conversion Rate settings; false for every call when the dashboard has none."
        },
        { name: 'is_test', type: 'boolean', note: 'A test call: from a test number or a "_" campaign.' },
        {
            name: 'ended_by',
            type: 'text',
            note: 'Agent ended the call, Customer ended the call, Ended due to inactivity timeout, Reached maximum conversation duration, or null.'
        },
        { name: 'qa_sentiment', type: 'text', note: QA_SENTIMENT },
        { name: 'rubric_results', type: 'jsonb', note: RUBRIC_RESULTS },
        { name: 'rubric_outcomes', type: 'jsonb', note: RUBRIC_OUTCOMES },
        {
            name: 'external_id',
            type: 'text',
            note: `The id the caller's system sent with an inbound call. ${LOADED_LATE}`
        },
        {
            name: 'sip_headers',
            type: 'jsonb',
            note: `SIP headers of an inbound SIP call, by header name. ${LOADED_LATE}`
        }
    ],
    strada_chats_history: [
        { name: 'chat_id', type: 'text', note: 'Unique id of the chat.' },
        { name: 'agent_id', type: 'text', note: 'The agent the chat started with.' },
        {
            name: 'customer_unique_id',
            type: 'text',
            note: 'Who the visitor is, when the website identified them, such as an email; null otherwise.'
        },
        { name: 'agent_name', type: 'text', note: "The agent's name." },
        { name: 'created_at', type: 'text', note: 'When the chat started.' },
        { name: 'handed_off_at', type: 'text', note: 'When the chat was handed to a person.' },
        { name: 'terminated_at', type: 'text', note: 'When the chat was ended.' },
        { name: 'abandoned_at', type: 'text', note: 'When the visitor left.' },
        {
            name: 'status',
            type: 'text',
            note: 'handedOff, terminated, abandoned or active, the first that applies in that order.'
        },
        { name: 'summary', type: 'text', note: 'The AI summary of the chat.' },
        {
            name: 'analysis',
            type: 'jsonb',
            note: "The agents' analysis fields, filled in after the chat. Keys differ per agent."
        },
        {
            name: 'transcript',
            type: 'jsonb',
            note: `{messages: [...]}, where each message is {role: "user" | "bot", content} or {role: "tool", toolName, toolArguments, toolResult} with arguments and result as text. Messages from people the chat was handed to aren't included. ${LOADED_LATE}`
        },
        { name: 'chat_variables', type: 'jsonb', note: 'Values passed in when the chat started.' },
        {
            name: 'session_variables',
            type: 'jsonb',
            note: "Values the agent's Set Variable steps stored during the chat, latest value per key. Null when none; blank in email reports."
        },
        {
            name: 'metadata',
            type: 'jsonb',
            note: 'What the website sent with the chat widget; debug true marks a test chat.'
        },
        {
            name: 'secure_metadata_keys',
            type: 'jsonb',
            note: 'Names of the secure values the website sent; the values are never shown.'
        },
        { name: 'is_test', type: 'boolean', note: 'A test chat.' },
        { name: 'qa_sentiment', type: 'text', note: QA_SENTIMENT },
        { name: 'rubric_results', type: 'jsonb', note: RUBRIC_RESULTS },
        { name: 'rubric_outcomes', type: 'jsonb', note: RUBRIC_OUTCOMES },
        {
            name: 'csat',
            type: 'boolean',
            note: 'The visitor said they were satisfied (true) or not (false); null if not asked.'
        }
    ]
};
//# sourceMappingURL=analytics-columns.js.map